import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import backgroundImage from '../../assets/PathianCurves.png';
import { authService } from '../../services/authService';
import { Dimmer, Loader, Message } from 'semantic-ui-react';

const containerStyle = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    minHeight: '87vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: '20px',
};

const headingStyle = {
    fontSize: '40px',
    color: '#00A7B5',
    margin: '10px',
    display: 'flex',
    justifyContent: 'flex-start',
    wordSpacing: '-0.2em',
};

const subheadingStyle = {
    fontSize: '25px',
    color: '#00A7B5',
    margin: '10px',
    display: 'flex',
    justifyContent: 'flex-start',
    wordSpacing: '-0.2em',
};

const linkStyle = {
    color: '#00A7B5',
    fontSize: '15px',
    fontWeight: 'bolder',
    textDecoration: 'none',
};

const formContainerStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '10px',
    padding: '30px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    width: '100%',
};

const inputGroupStyle = {
    marginBottom: '20px',
};

const labelStyle = {
    fontSize: '16px',
    color: '#254A5D',
    display: 'block',
    marginBottom: '5px',
    fontWeight: '500',
};

const inputStyle = {
    borderColor: '#00a7b5',
    borderRadius: '7px',
    borderStyle: 'solid',
    borderWidth: 'thin',
    height: '40px',
    width: '100%',
    padding: '0 10px',
    fontSize: '14px',
    boxShadow: '0.5px 0.5px 2px #00a7b5',
};

const buttonStyle = {
    backgroundColor: '#00a7b5',
    borderRadius: '5px',
    color: 'white',
    padding: '12px 30px',
    fontSize: '16px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '1px 1px 3px #00a7b5',
    width: '100%',
    marginTop: '10px',
};

const rowStyle = {
    display: 'flex',
    gap: '15px',
};

export const Signup = () => {
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        email: '',
        companyName: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [validationError, setValidationError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [marketplaceToken, setMarketplaceToken] = useState(null);
    const [marketplaceSubscription, setMarketplaceSubscription] = useState(null);
    const [isMarketplaceFlow, setIsMarketplaceFlow] = useState(false);
    const navigate = useNavigate();

    // Check for marketplace token in URL on mount
    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            setMarketplaceToken(token);
            setIsMarketplaceFlow(true);
            resolveMarketplaceToken(token);
        }
    }, [searchParams]);

    const resolveMarketplaceToken = async (token) => {
        setLoading(true);
        try {
            const result = await authService.resolveMarketplaceToken(token);
            if (result && !result.error) {
                setMarketplaceSubscription(result);
                // Pre-fill form with data from marketplace subscription
                setFormData(prev => ({
                    ...prev,
                    email: result.email || prev.email,
                    companyName: result.subscriptionName || prev.companyName,
                }));
            } else {
                setErrors([result?.message || 'Failed to resolve marketplace subscription. Please try again or contact support.']);
                setIsMarketplaceFlow(false);
                setMarketplaceToken(null);
            }
        } catch (err) {
            setErrors(['Failed to resolve marketplace subscription. Please try again or contact support.']);
            setIsMarketplaceFlow(false);
            setMarketplaceToken(null);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        setValidationError('');
        setErrors([]);
        setSuccessMessage('');
    };

    const validateForm = () => {
        // Basic validation - just ensure required fields are filled
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors([]);
        setValidationError('');
        setSuccessMessage('');

        const result = await authService.signup(
            formData.email,
            formData.firstName,
            formData.lastName,
            formData.companyName,
            formData.phone,
            formData.address,
            formData.city,
            formData.state,
            formData.zip,
            marketplaceToken
        );

        setLoading(false);

        if (result && result.status === 'SUCCESS') {
            // Show success message - different message for marketplace flow
            if (isMarketplaceFlow) {
                setSuccessMessage(result.message || 'Your Azure Marketplace subscription has been activated! Your API key will be available in your account dashboard.');
            } else {
                setSuccessMessage(result.message || 'Thank you for your interest! Our sales team will contact you shortly.');
            }
            // Clear form and marketplace state
            setFormData({
                email: '',
                companyName: '',
                firstName: '',
                lastName: '',
                phone: '',
                address: '',
                city: '',
                state: '',
                zip: '',
            });
            setMarketplaceToken(null);
            setMarketplaceSubscription(null);
        } else if (result && result.errors) {
            setErrors(result.errors);
        } else if (result && result.message) {
            setErrors([result.message]);
        } else {
            setErrors(['An error occurred during signup. Please try again.']);
        }
    };

    if (loading) {
        return (
            <Dimmer active={loading} inverted>
                <Loader active={loading} size="massive" inline />
            </Dimmer>
        );
    }

    return (
        <div style={containerStyle} className="Franklin-font">
            <div style={formContainerStyle}>
                <div style={{ marginBottom: '20px' }}>
                    <p style={headingStyle}>
                        {isMarketplaceFlow ? 'Complete Your Azure Marketplace Subscription' : 'Sign up for Pathian Analytics'}
                    </p>
                    <p style={subheadingStyle}>
                        {isMarketplaceFlow ? 'Please provide your contact details to activate your subscription.' : 'Tell us about your business.'}
                    </p>
                </div>

                {isMarketplaceFlow && marketplaceSubscription && (
                    <Message info>
                        <Message.Header>Azure Marketplace Subscription</Message.Header>
                        <p>Plan: {marketplaceSubscription.planId || 'Utility Benchmarking'}</p>
                    </Message>
                )}

                {successMessage && (
                    <Message positive>
                        <Message.Header>Success!</Message.Header>
                        <p>{successMessage}</p>
                    </Message>
                )}

                {(errors.length > 0 || validationError) && (
                    <Message negative>
                        <Message.Header>Signup Failed</Message.Header>
                        {validationError && <p>{validationError}</p>}
                        {errors.length > 0 && (
                            <ul>
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        )}
                    </Message>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>Email *</label>
                        <input
                            style={inputStyle}
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>Company Name *</label>
                        <input
                            style={inputStyle}
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={rowStyle}>
                        <div style={{ ...inputGroupStyle, flex: 1 }}>
                            <label style={labelStyle}>First Name *</label>
                            <input
                                style={inputStyle}
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div style={{ ...inputGroupStyle, flex: 1 }}>
                            <label style={labelStyle}>Last Name *</label>
                            <input
                                style={inputStyle}
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>Phone *</label>
                        <input
                            style={inputStyle}
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>Address *</label>
                        <input
                            style={inputStyle}
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={rowStyle}>
                        <div style={{ ...inputGroupStyle, flex: 2 }}>
                            <label style={labelStyle}>City *</label>
                            <input
                                style={inputStyle}
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div style={{ ...inputGroupStyle, flex: 1 }}>
                            <label style={labelStyle}>State *</label>
                            <input
                                style={inputStyle}
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                maxLength="2"
                                required
                            />
                        </div>
                        <div style={{ ...inputGroupStyle, flex: 1 }}>
                            <label style={labelStyle}>Zip *</label>
                            <input
                                style={inputStyle}
                                type="text"
                                name="zip"
                                value={formData.zip}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <button type="submit" style={buttonStyle}>
                        {isMarketplaceFlow ? 'Activate Subscription' : 'Create Account'}
                    </button>
                </form>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <span style={{ color: '#254A5D', fontSize: '14px' }}>
                        Already have an account?{' '}
                    </span>
                    <a href="/" style={linkStyle}>
                        Sign in
                    </a>
                </div>
            </div>
        </div>
    );
};
